import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send, FileText, Sparkles, Check } from "lucide-react";
import { getCateringPackages, submitCateringRequest } from "@/lib/api";
import { useContent } from "@/lib/content";
import { Overline } from "@/components/Overline";
import { Seo } from "@/components/Seo";

const ease = [0.16, 1, 0.3, 1];
const CATERING_TYPES = ["Buffet", "Fingerfood", "Pizza", "Pasta", "Grill", "Gemischt"];
const OCCASIONS = ["Geburtstag", "Hochzeit", "Firmenfeier", "Familienfeier", "Jubiläum", "Sonstiges"];
const BUDGETS = ["bis 500 €", "500 – 1.000 €", "1.000 – 2.500 €", "2.500 – 5.000 €", "über 5.000 €"];

const initialForm = {
  name: "", email: "", phone: "", occasion: "", event_date: "", event_time: "",
  location: "", guests: "", catering_type: "", package: "", budget: "", delivery_time: "",
  setup: false, staff: false, tableware: false, drinks: false, allergies: "", message: "", consent: false,
};

export default function Catering() {
  const c = useContent();
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { getCateringPackages().then(setPackages).catch(() => setPackages([])); }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const selectedPkg = packages.find((p) => p.name === form.package);
  const guests = parseInt(form.guests, 10) || 0;
  const estimate = selectedPkg && guests > 0 ? selectedPkg.price_per_person * guests : 0;
  const estimateStr = estimate > 0 ? `ca. ${estimate.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : "";

  const choosePackage = (name) => {
    set("package", name);
    document.getElementById("anfrage")?.scrollIntoView({ behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Bitte stimmen Sie der Datenschutzerklärung zu."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.set("estimated_price", estimateStr);
      if (file) fd.append("location_file", file);
      await submitCateringRequest(fd);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anfrage konnte nicht gesendet werden.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-wine/15 rounded px-3 py-2.5 text-sm focus:border-wine focus:outline-none";
  const labelCls = "text-xs tracking-[0.12em] uppercase text-ink-muted mb-1.5 block";

  return (
    <div data-testid="catering-page" className="bg-cream">
      <Seo page="catering" />
      <section className={`pt-44 pb-16 relative ${c.images.cateringHero ? "" : "bg-sand grain"}`}>
        {c.images.cateringHero && (
          <>
            <div className="absolute inset-0"><img src={c.images.cateringHero} alt="" className="w-full h-full object-cover" /></div>
            <div className="absolute inset-0 bg-ink/60" />
          </>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Overline light={!!c.images.cateringHero} className="!flex justify-center">Catering & Events</Overline>
          <h1 className={`font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight ${c.images.cateringHero ? "text-cream" : "text-ink"}`}>{c.text.catering.heroTitle}</h1>
          <p className={`font-light mt-5 max-w-2xl mx-auto ${c.images.cateringHero ? "text-cream/85" : "text-ink-soft"}`}>
            {c.text.catering.heroText}
          </p>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="py-20 md:py-28" data-testid="catering-packages">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <Overline className="!flex justify-center">Unsere Pakete</Overline>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink">Catering-Pakete</h2>
            <p className="text-ink-soft font-light mt-3">Preis pro Person – individuell anpassbar.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1, ease }}
                className={`bg-white border p-8 flex flex-col transition-all duration-300 ${form.package === p.name ? "border-wine shadow-xl" : "border-gold/20 hover:shadow-lg"}`} data-testid={`package-${p.id}`}>
                <h3 className="font-serif text-2xl text-ink mb-2">{p.name}</h3>
                <p className="font-serif text-4xl text-gold-dark mb-4">{p.price_per_person.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €<span className="text-sm text-ink-muted font-sans"> / Person</span></p>
                <p className="text-ink-soft font-light text-sm leading-relaxed flex-1 mb-6">{p.description}</p>
                <button onClick={() => choosePackage(p.name)} data-testid={`choose-${p.id}`}
                  className={`btn-pop px-6 py-3 uppercase text-[11px] tracking-[0.2em] inline-flex items-center justify-center gap-2 ${form.package === p.name ? "bg-wine text-cream" : "border border-wine text-wine hover:bg-wine hover:text-cream"}`}>
                  {form.package === p.name ? <><Check size={15} /> Ausgewählt</> : "Dieses Paket wählen"}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REQUEST FORM */}
      <section id="anfrage" className="py-20 md:py-28 bg-sand grain scroll-mt-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {done ? (
            <div className="bg-white border border-gold/20 p-10 text-center" data-testid="catering-success">
              <CheckCircle2 className="mx-auto text-green-600 mb-5" size={56} strokeWidth={1.3} />
              <h2 className="font-serif font-light text-3xl text-ink mb-3">Vielen Dank für Ihre Anfrage!</h2>
              <p className="text-ink-soft font-light">Wir prüfen Ihre Angaben und melden uns mit einem persönlichen Angebot bei Ihnen. Wir freuen uns auf Ihre Veranstaltung!</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-10">
                <Overline className="!flex justify-center">Anfrage</Overline>
                <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink">Catering anfragen</h2>
              </div>
              <form onSubmit={onSubmit} className="bg-white border border-gold/20 p-6 sm:p-10 space-y-6" data-testid="catering-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div><label className={labelCls}>Name *</label><input data-testid="cat-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>E-Mail *</label><input data-testid="cat-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Telefon *</label><input data-testid="cat-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></div>
                  <div>
                    <label className={labelCls}>Anlass</label>
                    <select data-testid="cat-occasion" value={form.occasion} onChange={(e) => set("occasion", e.target.value)} className={inputCls}>
                      <option value="">Bitte wählen…</option>{OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div><label className={labelCls}>Veranstaltungsdatum</label><input data-testid="cat-date" type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Uhrzeit</label><input data-testid="cat-time" type="time" value={form.event_time} onChange={(e) => set("event_time", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Anzahl Gäste</label><input data-testid="cat-guests" type="number" min="1" value={form.guests} onChange={(e) => set("guests", e.target.value)} className={inputCls} /></div>
                  <div>
                    <label className={labelCls}>Art des Caterings</label>
                    <select data-testid="cat-type" value={form.catering_type} onChange={(e) => set("catering_type", e.target.value)} className={inputCls}>
                      <option value="">Bitte wählen…</option>{CATERING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Paket</label>
                    <select data-testid="cat-package" value={form.package} onChange={(e) => set("package", e.target.value)} className={inputCls}>
                      <option value="">Kein Paket / individuell</option>
                      {packages.map((p) => <option key={p.id} value={p.name}>{p.name} ({p.price_per_person.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €/Pers.)</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Budget</label>
                    <select data-testid="cat-budget" value={form.budget} onChange={(e) => set("budget", e.target.value)} className={inputCls}>
                      <option value="">Bitte wählen…</option>{BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div><label className={labelCls}>Gewünschte Abholzeit</label><input data-testid="cat-delivery" value={form.delivery_time} onChange={(e) => set("delivery_time", e.target.value)} className={inputCls} placeholder="z. B. 17:30 Uhr" /></div>
                </div>

                {/* LIVE ESTIMATE */}
                {estimateStr && (
                  <div className="bg-gold/10 border border-gold/30 rounded p-5 flex items-center gap-4" data-testid="cat-estimate">
                    <Sparkles className="text-gold-dark shrink-0" size={28} />
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] text-ink-muted">Unverbindliche Preisschätzung</p>
                      <p className="font-serif text-3xl text-wine">{estimateStr}</p>
                      <p className="text-xs text-ink-muted">{selectedPkg.name} · {guests} Gäste × {selectedPkg.price_per_person.toLocaleString("de-DE", { minimumFractionDigits: 2 })} € (zzgl. Optionen, finales Angebot folgt)</p>
                    </div>
                  </div>
                )}

                <div className="bg-gold/10 border border-gold/30 rounded p-3 text-sm text-ink-soft flex items-center gap-2" data-testid="cat-pickup-note">
                  <CheckCircle2 size={18} className="text-gold-dark shrink-0" /> Catering ist aktuell <strong>nur zur Selbstabholung</strong> erhältlich.
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[["tableware", "Geschirr & Besteck"], ["drinks", "Getränke"]].map(([k, lbl]) => (
                    <label key={k} className="flex items-center gap-2 text-sm text-ink-soft border border-wine/15 rounded px-3 py-2.5 cursor-pointer">
                      <input data-testid={`cat-${k}`} type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} /> {lbl}
                    </label>
                  ))}
                </div>

                <div><label className={labelCls}>Allergien & Ernährungsformen</label><input data-testid="cat-allergies" value={form.allergies} onChange={(e) => set("allergies", e.target.value)} className={inputCls} placeholder="z. B. vegetarisch, vegan, glutenfrei, Nussallergie" /></div>
                <div><label className={labelCls}>Ihre Wünsche (Freitext)</label><textarea data-testid="cat-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls + " resize-y"} placeholder="Besondere Wünsche, Ablauf, Dekoration…" /></div>

                <div>
                  <label className={labelCls}>Foto / Plan der Location (optional)</label>
                  <label className="cursor-pointer flex items-center gap-2 bg-cream border border-wine/20 rounded px-3 py-2.5 text-sm text-ink-soft hover:border-wine transition-colors">
                    <FileText size={16} className="text-wine shrink-0" />
                    <span className="truncate">{file ? file.name : "Datei wählen (Bild oder PDF)"}</span>
                    <input type="file" accept="image/*,application/pdf" data-testid="cat-file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  </label>
                </div>

                <label className="flex items-start gap-2 text-sm text-ink-soft">
                  <input data-testid="cat-consent" type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" />
                  <span>Ich habe die <a href="/datenschutz" target="_blank" className="text-wine underline">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Daten zu. *</span>
                </label>

                <button type="submit" disabled={submitting} data-testid="cat-submit" className="btn-pop w-full bg-wine text-cream hover:bg-wine-dark px-8 py-4 uppercase text-xs tracking-[0.25em] inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Anfrage absenden
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
